import React, { useContext, useEffect } from 'react';

import { TrackEventContext } from 'common/containers/EventContainer';
import ReadmeLandingHelmet from 'common/helmets/ReadmeLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';
import ReadmeOwl from 'img/landing/readme-owl.webp';
import ReadmeWordmark from 'img/landing/readme-wordmark.webp';

import 'css/components/www/_LandingCustomersPage.scss';

const ReadmeLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed ReadMe Case Study');
  }, [trackEvent]);

  return (
    <div className="landingReadme landingCustomersPage">
      <ReadmeLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How ReadMe uses Canny to build the most impactful features for their customers" />
          <div className="headerImage">
            <LazyLoadedImage alt="Readme logo" src={ReadmeOwl} />
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              If your business has thousands of customers, you're probably familiar with a large
              volume of customer requests. ReadMe definitely&nbsp;is.
            </p>
            <p>
              David Reed is head of sales and customer success at ReadMe. He always has his eye on
              their customers. While he wanted to solve the biggest problems their customers were
              facing, understanding what those problems were always proved&nbsp;difficult.
            </p>
            <p className="quote">
              &ldquo;We had lots of requests but didn’t actually know what customers wanted. It took
              forever to decide on what to build&nbsp;next.&rdquo;
            </p>
            <p>
              Inside ReadMe, the link for "Feature Request" used to direct customers to a form. Over
              time, feature requests piled up. The team had to spend a significant amount of time
              manually keeping track of requests. It was a big time commitment for a small team of
              ten. So, David sought out a new solution.
            </p>
            <h2 className="sectionTitle">Challenge</h2>
            <p>
              Prior to Canny, David and his team manually tracked feedback in many different places:
              Asana, Excel, Trello, and&nbsp;more.
            </p>
            <p className="quote">&ldquo;We'd tried everything, nothing&nbsp;worked.&rdquo;</p>
            <p>
              Process changes can be very disruptive to a team's productivity but they hadn't found
              a solution. They wanted a product&nbsp;that:
            </p>
            <ul>
              <li>
                Helps understand the biggest problems customers were facing to inform
                product&nbsp;decisions.
              </li>
              <li>
                Was customer-facing so the team wouldn't have to manually track&nbsp;requests.
              </li>
              <li>
                Aggregates requests in one place, removing the issue of duplicate&nbsp;requests.
              </li>
            </ul>
            <h2 className="sectionTitle">Solution</h2>
            <p>David described their process before Canny&nbsp;as:</p>
            <p className="quote">&ldquo;All over the place and&nbsp;confusing.&rdquo;</p>
            <p>
              ReadMe simply replaced their feature request form with Canny. When customers reach out
              through other means, ReadMe directs them to&nbsp;Canny.
            </p>
            <p>
              Their only concern was that customers wouldn't use it. However, after populating their
              board with a few requests, it quickly filled up with feedback directly
              from&nbsp;customers.
            </p>
            <p>
              With Canny, they have everything in one place and can refer to it when building their
              roadmap every&nbsp;month.
            </p>
            <p className="quote">
              &ldquo;A significant amount of our roadmap just creates itself. All the tracking and
              guesswork are&nbsp;done.&rdquo;
            </p>
            <h2 className="sectionTitle">Results</h2>
            <p>
              As a sales/customer success person, David hears requests every day. Nearly every
              customer interaction involves a feature&nbsp;request.
            </p>
            <p className="quote">
              &ldquo;Documenting requests then fighting for them on behalf of the customer
              internally was a huge percentage of my time. Canny allows our customers to request
              features in their own words and sell the features for&nbsp;me.&rdquo;
            </p>
            <p>When building out their roadmap, ReadMe considers several&nbsp;inputs:</p>
            <ul>
              <li>Ideas from the&nbsp;team</li>
              <li>Bug fixes</li>
              <li>Iterating on the current&nbsp;product</li>
              <li>Things customers&nbsp;want</li>
            </ul>
            <p>
              Canny has become the oracle for the "things customers want". Marc, head of engineering
              at ReadMe, is one of the main Canny users at ReadMe. Now, Marc and the team are able
              to make product decisions with&nbsp;confidence.
            </p>
            <p>
              Importantly, their customers feel more respected. The ReadMe team is listening and
              cares about their&nbsp;feedback.
            </p>
            <p className="quote">
              &ldquo;If you want to focus resources on the highest impact features that scale to the
              most customers, try Canny. We&nbsp;love&nbsp;it.&rdquo;
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage
              alt="Readme Wordmark"
              className="readmeWordmark"
              src={ReadmeWordmark}
            />
            Businesses use Readme to build a great developer experience. Use your API documentation
            to build great&nbsp;partnerships.
            <a href="https://readme.io/" rel="noopener" target="_blank">
              readme.io
            </a>
            <div className="stat">
              <UppercaseHeader>Customers</UppercaseHeader>
              <p>2,500+ teams</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Employees</UppercaseHeader>
              <p>10</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Years on Canny</UppercaseHeader>
              <p>1</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>1,000+</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};

export default ReadmeLanding;
